'use client';
import { usePrivy } from '@privy-io/react-auth';
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useDisconnect } from 'wagmi';

import { clearAuthTokens } from '@/lib/authToken';
import { authApi } from '@/services/api';
import { useAppDispatch, useAppSelector } from '@/store';
import { clearAuth, updateUser } from '@/store/slices/auth.slice';
import type { User } from '@/types';
import { deleteWCIDB, saveWCSessionForUser } from '@/utils/wcSession.utils';

// Query Keys Factory
export const AUTH_QUERY_KEYS = {
  all: ['auth'] as const,
  user: () => [...AUTH_QUERY_KEYS.all, 'user'] as const,
};

/** Keys Privy / WalletConnect / wagmi may persist; must not survive logout (cross-user leakage). */
function shouldRemoveWalletStorageKey(key: string): boolean {
  const lower = key.toLowerCase();
  return (
    key.startsWith('clob_creds_') ||
    key.startsWith('wc@') ||
    key.startsWith('wagmi.') ||
    key.startsWith('wagmi-') ||
    key.startsWith('@w3m/') ||
    key.startsWith('@walletconnect/') ||
    key.startsWith('WALLETCONNECT_') ||
    key.startsWith('W3M_') ||
    key.startsWith('privy:') ||
    key.includes('privy-') ||
    lower.includes('walletconnect')
  );
}

/** Snapshot keys first — iterating by index while storage mutates can skip entries. */
function purgeWalletRelatedLocalStorage(): void {
  for (const key of Object.keys(localStorage)) {
    if (shouldRemoveWalletStorageKey(key)) {
      localStorage.removeItem(key);
    }
  }
}

/**
 * Privy may write `privy:connections` / `privy:caid` after `logout()` resolves; purge across
 * microtask turns then once more right before navigation.
 */
async function purgeWalletLocalStorageAggressively(): Promise<void> {
  for (let i = 0; i < 3; i++) {
    purgeWalletRelatedLocalStorage();
    await Promise.resolve();
  }
}

// Fetch current user via status endpoint
export const useCurrentUser = (options?: Partial<UseQueryOptions<User | null>>) => {
  const accessToken = useAppSelector((state) => state.auth.accessToken);

  return useQuery<User | null>({
    queryKey: AUTH_QUERY_KEYS.user(),
    queryFn: async () => {
      const status = await authApi.getStatus();
      return status.authenticated ? status.user : null;
    },
    enabled: !!accessToken,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
    ...options,
  });
};

// Login with email/password mutation (Handles synchronization)
export const useLogin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ idToken, userName }: { idToken: string; userName?: string }) =>
      authApi.login(idToken, userName),
    onSuccess: (response: any) => {
      queryClient.setQueryData(AUTH_QUERY_KEYS.user(), response.user);
    },
  });
};

// Logout mutation - handles complete teardown (Privy, Backend, Redux, wagmi)
export const useLogout = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const { logout: privyLogout, user: privyUser } = usePrivy();
  const { disconnectAsync } = useDisconnect();

  return useMutation({
    mutationKey: ['logout'],
    mutationFn: async () => {
      const withTimeout = <T>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
        return Promise.race([
          promise,
          new Promise<T>((_, reject) =>
            setTimeout(() => reject(new Error(`Timeout: ${label}`)), ms)
          ),
        ]);
      };

      // 1. Attempt to clear Backend API session first
      try {
        await withTimeout(authApi.logout(), 2000, 'authApi.logout');
      } catch (err) {
        console.warn('[Logout] Backend session cleanup failed:', err);
      }

      // 2. Snapshot the WC session for the current user
      if (typeof window !== 'undefined' && privyUser?.id) {
        try {
          await withTimeout(saveWCSessionForUser(privyUser.id), 2000, 'saveWCSessionForUser');
          localStorage.setItem('__pm_last_uid__', privyUser.id);
        } catch (err) {
          console.warn('[Logout] WC save failed:', err);
        }
      }

      // 3. Disconnect wagmi / WalletConnect
      try {
        await withTimeout(disconnectAsync(), 2000, 'disconnectAsync');
      } catch (err) {
        console.warn('[Logout] wagmi disconnect failed:', err);
      }

      // 4. Clear Privy session
      try {
        await withTimeout(privyLogout(), 3000, 'privyLogout');
      } catch (err) {
        console.warn('[Logout] Privy session cleanup failed:', err);
      }

      // First storage pass while still in the mutation tick
      if (typeof window !== 'undefined') {
        purgeWalletRelatedLocalStorage();
      }

      if (typeof window === 'undefined') {
        dispatch(clearAuth());
        queryClient.setQueryData(AUTH_QUERY_KEYS.user(), null);
        queryClient.clear();
        return;
      }

      // ── 5. Purge wallet-related localStorage (multi-pass) ──
      await purgeWalletLocalStorageAggressively();

      // ── 6. Purge sessionStorage ──────────────────────────────────────────
      try {
        sessionStorage.clear();
      } catch (err) {
        console.warn('[Logout] sessionStorage clear failed:', err);
      }

      // ── 7. Delete auxiliary wallet IndexedDB databases ───────────────────
      try {
        if ('indexedDB' in window && typeof indexedDB.databases === 'function') {
          const shouldDelete = (name: string): boolean => {
            const n = name.toLowerCase();
            return (
              n.includes('walletconnect') ||
              n.includes('wallet_connect') ||
              n.includes('wagmi') ||
              n.includes('wc-') ||
              n.includes('w3m') ||
              n.includes('privy') ||
              n.includes('reown')
            );
          };
          const dbs = await indexedDB.databases();
          await withTimeout(
            Promise.all(
              dbs
                .map((db) => db.name)
                .filter((n): n is string => !!n && shouldDelete(n))
                .map(
                  (n) =>
                    new Promise<void>((res) => {
                      const r = indexedDB.deleteDatabase(n);
                      r.onsuccess = r.onerror = r.onblocked = () => res();
                    })
                )
            ),
            2000,
            'indexedDB cleanup'
          );
        }
      } catch (err) {
        console.warn('[Logout] IndexedDB cleanup failed:', err);
      }

      // IndexedDB deletion can unblock WC/Privy async writes — purge storage again before navigate.
      await purgeWalletLocalStorageAggressively();

      // ── 8. Proactively wipe local tokens and Redux state ────────────────
      // We do this right before reload so that if it triggers component unmounts,
      // the reload is already queued up and guaranteed to execute.
      dispatch(clearAuth());
      clearAuthTokens();
      queryClient.setQueryData(AUTH_QUERY_KEYS.user(), null);
      queryClient.clear();

      // ── 9. Hard reload to clear all in-memory state ─────────────────────
      // Privy session, wagmi connectors, WalletConnect handshakes, SDK caches.
      // window.location.replace prevents back-button from restoring stale state.
      window.location.replace('/');

      // Add a small delay so the function doesn't resolve immediately and let the unmount cause a crash
      await new Promise((resolve) => setTimeout(resolve, 500));
    },
  });
};
// Update display name mutation
export const useUpdateDisplayName = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  return useMutation({
    mutationFn: (displayName: string) => authApi.updateDisplayName(displayName),
    onSuccess: (updatedUser) => {
      // Only extract the display name dynamically to prevent clobbering other unaffected fields
      // if the backend response is a partial object.
      const newName = updatedUser.name;

      // Update Redux store partially
      dispatch(updateUser({ name: newName }));

      // Update TanStack Query cache partially
      queryClient.setQueryData<User | null>(AUTH_QUERY_KEYS.user(), (old) => {
        if (!old) return old;
        return { ...old, name: newName };
      });
    },
  });
};
